import { ajax } from "lib/observable";
import { Observable } from "rxjs";
import "rxjs/add/operator/map";
import siteConfig from "site-config";
import { stringify } from "querystring";
import { SIZE_FAMILIES, SIZE_OVERRIDE } from "constants/action-types";
import { add as snackbarAdd } from "../actions/snackbars";
import { formatMessage } from "lib/globalize-util";

export const sizesOverridesEpic = (action$, store) =>
    action$
        .ofType(SIZE_OVERRIDE.FETCH.START)
        .map((action) => action.payload)
        .concatMap(() => {
            const query = {
                __limit: 1000,
            };
            return ajax(`${siteConfig.api}/api/v3.0/items/sizes/overrides?${stringify(query)}`)
                .map(({ response }) => {
                    return { overrides: response };
                })
                .flatMap(({ overrides }) => {
                    const queryParams = {
                        __limit: 200,
                    };
                    return ajax(`${siteConfig.api}/api/v3.0/items/sizes/families?${stringify(queryParams)}`)
                        .map(({ response }) => {
                            const families = response
                                .slice()
                                .sort((f1, f2) => f1.name.toLowerCase().localeCompare(f2.name));
                            return {
                                sizeOverrides: overrides,
                                families,
                            };
                        })
                        .flatMap(({ sizeOverrides, families }) => {
                            return ajax(`${siteConfig.api}/brands?`).map(({ response }) => {
                                return {
                                    brands: response,
                                    sizeOverrides,
                                    families,
                                };
                            });
                        });
                })
                .catch((error) =>
                    Observable.merge(
                        Observable.of({
                            error: true,
                            payload: error,
                            type: SIZE_OVERRIDE.FETCH.SUCCESS,
                        }),
                        Observable.of(
                            snackbarAdd({ message: formatMessage("There was an error fetching size families") })
                        )
                    )
                );
        })
        .flatMap(({ sizeOverrides, families, brands }) => {
            sizeOverrides.forEach((override) => {
                if (override.brand) {
                    const brand = brands.find((b) => b._id === override.brand);
                    override.brand_name = brand.name;
                } else {
                    override.brand_name = formatMessage("Global");
                }
            });

            const sizeFamilySortMap = {};
            families.forEach(({ name, sizes = [] }) => {
                sizeFamilySortMap[name] = {};

                sizes.forEach(({ size, sort }) => {
                    sizeFamilySortMap[name][size] = sort;
                });
            });

            // sort size overrides
            sizeOverrides.sort((a, b) => {
                if (a.size_family < b.size_family) {
                    return -1;
                } else if (a.size_family > b.size_family) {
                    return 1;
                } else {
                    // same size family, so let's sort by size sort
                    const aSort = sizeFamilySortMap[a.size_code] || Infinity;
                    const bSort = sizeFamilySortMap[b.size_code] || Infinity;

                    return aSort - bSort;
                }
            });

            return [
                {
                    payload: families,
                    type: SIZE_FAMILIES.FETCH.SUCCESS,
                },
                {
                    payload: sizeOverrides,
                    type: SIZE_OVERRIDE.FETCH.SUCCESS,
                },
            ];
        })
        .catch((error) =>
            Observable.merge(
                Observable.of({
                    error: true,
                    payload: error,
                    type: SIZE_OVERRIDE.FETCH.SUCCESS,
                }),
                Observable.of(snackbarAdd({ message: formatMessage("There was an error fetching size families") }))
            )
        );

export const updateSizeFamiliesEpic = (action$, store) =>
    action$
        .ofType(SIZE_OVERRIDE.UPDATE.START)
        .map((action) => {
            return action.payload;
        })
        .concatMap((sizeFamily) => {
            const { _id, brand_name } = sizeFamily;
            return ajax({
                method: "PUT",
                body: sizeFamily,
                url: `${siteConfig.api}/api/v3.0/items/sizes/overrides/${_id}`,
            })
                .flatMap(({ response }) => {
                    const updatedOverride = response;
                    updatedOverride.brand_name = brand_name;
                    return [
                        {
                            payload: updatedOverride,
                            type: SIZE_OVERRIDE.UPDATE.COMPLETED,
                        },
                    ];
                })
                .catch((error) =>
                    Observable.merge(
                        Observable.of({
                            error: true,
                            payload: error,
                            type: SIZE_OVERRIDE.UPDATE.COMPLETED,
                        }),
                        Observable.of(
                            snackbarAdd({ message: formatMessage("There was an error updating size overrides") })
                        )
                    )
                );
        });

export const createSizeOverrideEpic = (action$, store) =>
    action$
        .ofType(SIZE_OVERRIDE.CREATE.START)
        .map((action) => {
            return action.payload;
        })
        .concatMap((sizeFamily) => {
            return ajax({
                method: "POST",
                body: sizeFamily,
                url: `${siteConfig.api}/api/v3.0/items/sizes/overrides`,
            })
                .flatMap(({ response }) => [
                    {
                        payload: response,
                        type: SIZE_OVERRIDE.CREATE.COMPLETED,
                    },
                ])
                .catch((error) =>
                    Observable.merge(
                        Observable.of({
                            error: true,
                            payload: error,
                            type: SIZE_OVERRIDE.CREATE.COMPLETED,
                        }),
                        Observable.of(
                            snackbarAdd({ message: formatMessage("There was an error creating size override") })
                        )
                    )
                );
        });

export const deleteSizeOverrideEpic = (action$, store) =>
    action$
        .ofType(SIZE_OVERRIDE.DELETE.START)
        .map((action) => {
            return action.payload;
        })
        .concatMap((id) => {
            return ajax({
                url: `${siteConfig.api}/api/v3.0/items/sizes/overrides/${id}`,
                method: "DELETE",
            })
                .flatMap(({ response }) => {
                    return [
                        {
                            payload: response,
                            type: SIZE_OVERRIDE.DELETE.COMPLETED,
                        },
                    ];
                })
                .catch((error) => {
                    return Observable.merge(
                        Observable.of({
                            error: true,
                            payload: error,
                            type: SIZE_OVERRIDE.DELETE.COMPLETED,
                        }),
                        Observable.of(
                            snackbarAdd({ message: formatMessage("There was an error deleting size override") })
                        )
                    );
                });
        });
